import React, { useEffect, useRef, useState } from 'react';

// Import jsNES correctly - it doesn't export NES as a named export
declare global {
  interface Window {
    jsnes: any;
  }
}

const jsnesLib: any = null;

const NESEmulator: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nesRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [availableRoms, setAvailableRoms] = useState<string[]>([]);
  const [selectedRom, setSelectedRom] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);

  // Audio context for sound
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    // Load available ROMs from the server
    fetchAvailableRoms();
    
    // Initialize audio context
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      audioContextRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
  }, []);

  const fetchAvailableRoms = async () => {
    try {
      const response = await fetch('/api/roms/nes');
      const data = await response.json();
      setAvailableRoms(data.files || []);
    } catch (error) {
      console.error('Failed to fetch ROMs:', error);
      setAvailableRoms([]);
    }
  };

  const initializeNES = async () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Create image data for the NES screen (256x240)
    const imageData = ctx.createImageData(256, 240);

    try {
      // Load jsNES dynamically first
      const jsnes = await import('jsnes');
      const NESConstructor = jsnes.default?.NES || jsnes.NES || (jsnes as any).default || jsnes;
      
      console.log('jsNES loaded:', jsnes);
      console.log('NES Constructor:', NESConstructor);
      
      nesRef.current = new NESConstructor({
        onFrame: (frameBuffer: number[]) => {
          // Convert NES frame buffer to canvas image data
          for (let i = 0; i < frameBuffer.length; i++) {
            const pixel = frameBuffer[i];
            imageData.data[i * 4] = (pixel >> 16) & 0xff;     // Red
            imageData.data[i * 4 + 1] = (pixel >> 8) & 0xff;  // Green
            imageData.data[i * 4 + 2] = pixel & 0xff;         // Blue
            imageData.data[i * 4 + 3] = 0xff;                 // Alpha
          }
          ctx.putImageData(imageData, 0, 0);
        },
        onAudioSample: () => {
          // Handle audio samples - simplified for now
        }
      });
      
      setIsLoaded(true);
    } catch (err) {
      setError(`Failed to initialize NES emulator: ${err}`);
      console.error('NES initialization error:', err);
    }
  };

  const loadRom = async (romName: string) => {
    if (!nesRef.current) {
      initializeNES();
    }

    if (!nesRef.current) {
      setError('NES emulator not initialized');
      return;
    }

    try {
      setError('');
      // Properly encode the ROM filename for URL
      const encodedRomName = encodeURIComponent(romName);
      console.log(`Fetching ROM from: /roms/nes/${encodedRomName}`);
      
      const response = await fetch(`/roms/nes/${encodedRomName}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/octet-stream',
        },
      });
      
      console.log(`Response status: ${response.status}, Content-Type: ${response.headers.get('content-type')}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load ROM: ${response.status} ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const romData = new Uint8Array(arrayBuffer);
      
      console.log(`Loading ROM: ${romName}, Size: ${romData.length} bytes`);
      
      // Check the first few bytes (NES ROM header)
      const header = Array.from(romData.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' ');
      console.log(`ROM header: ${header}`);
      
      // Check for NES ROM signature (should start with "NES" + 0x1A)
      if (romData.length >= 4) {
        const nesSignature = romData[0] === 0x4E && romData[1] === 0x45 && romData[2] === 0x53 && romData[3] === 0x1A;
        console.log(`NES signature valid: ${nesSignature}`);
        console.log(`First 4 bytes: ${romData[0]}, ${romData[1]}, ${romData[2]}, ${romData[3]}`);
      }
      
      // Validate ROM size (NES ROMs should be at least 16KB)
      if (romData.length < 16384) {
        throw new Error(`ROM file too small (${romData.length} bytes). Not a valid NES ROM.`);
      }
      
      // Try loading the ROM and catch specific jsNES errors
      try {
        // First try with Uint8Array
        nesRef.current.loadROM(romData);
      } catch (nesError: unknown) {
        console.error('jsNES loadROM error with Uint8Array:', nesError);
        
        // Try alternative: convert to binary string
        try {
          const binaryString = Array.from(romData).map(byte => String.fromCharCode(byte)).join('');
          console.log('Trying to load ROM as binary string...');
          nesRef.current.loadROM(binaryString);
        } catch (nesError2: unknown) {
          console.error('jsNES loadROM error with binary string:', nesError2);
          const errorMessage = nesError instanceof Error ? nesError.message : String(nesError);
          throw new Error(`jsNES ROM loading failed: ${errorMessage}`);
        }
      }
      setSelectedRom(romName);
      setIsPlaying(true);
      
      // Start the game loop
      startGameLoop();
    } catch (error) {
      setError(`Error loading ROM: ${error}`);
      console.error('ROM loading error:', error);
    }
  };

  const testRomLoad = () => {
    if (!nesRef.current) {
      initializeNES();
    }
    
    if (!nesRef.current) {
      setError('NES emulator not initialized');
      return;
    }

    // Test with a minimal valid NES ROM header
    const testRom = new Uint8Array([
      0x4E, 0x45, 0x53, 0x1A, // "NES" + 0x1A
      0x02, 0x01, 0x01, 0x00, // 2 PRG banks, 1 CHR bank, mapper 0
      0x00, 0x00, 0x00, 0x00, // Padding
      0x00, 0x00, 0x00, 0x00, // Padding
      // Add minimal PRG ROM data (32KB = 2 banks * 16KB)
      ...new Array(32768).fill(0),
      // Add CHR ROM data (8KB = 1 bank * 8KB)  
      ...new Array(8192).fill(0)
    ]);
    
    try {
      nesRef.current.loadROM(testRom);
      console.log('Test ROM loaded successfully!');
      setError('Test ROM loaded successfully! The emulator is working.');
    } catch (error) {
      console.error('Test ROM failed:', error);
      setError(`Test ROM failed: ${error}`);
    }
  };

  const startGameLoop = () => {
    if (!nesRef.current) return;

    const frame = () => {
      if (isPlaying && nesRef.current) {
        nesRef.current.frame();
        requestAnimationFrame(frame);
      }
    };
    
    requestAnimationFrame(frame);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!nesRef.current || !isPlaying) return;

    const keyMap: { [key: string]: number } = {
      'ArrowUp': 0x10,    // Up
      'ArrowDown': 0x20,  // Down
      'ArrowLeft': 0x40,  // Left
      'ArrowRight': 0x80, // Right
      'KeyZ': 0x01,       // A button
      'KeyX': 0x02,       // B button
      'Enter': 0x08,      // Start
      'ShiftRight': 0x04, // Select
    };

    const keyCode = keyMap[event.code];
    if (keyCode) {
      nesRef.current.buttonDown(1, keyCode); // Player 1
      event.preventDefault();
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (!nesRef.current || !isPlaying) return;

    const keyMap: { [key: string]: number } = {
      'ArrowUp': 0x10,
      'ArrowDown': 0x20,
      'ArrowLeft': 0x40,
      'ArrowRight': 0x80,
      'KeyZ': 0x01,
      'KeyX': 0x02,
      'Enter': 0x08,
      'ShiftRight': 0x04,
    };

    const keyCode = keyMap[event.code];
    if (keyCode) {
      nesRef.current.buttonUp(1, keyCode); // Player 1
      event.preventDefault();
    }
  };

  const togglePause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      startGameLoop();
    }
  };

  const reset = () => {
    if (nesRef.current) {
      nesRef.current.reset();
      if (!isPlaying) {
        setIsPlaying(true);
        startGameLoop();
      }
    }
  };

  return (
    <div className="nes-emulator" tabIndex={0} onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
      <div className="nes-controls" style={{ marginBottom: '10px' }}>
        <select 
          value={selectedRom} 
          onChange={(e) => setSelectedRom(e.target.value)}
          disabled={isPlaying}
        >
          <option value="">Select a ROM...</option>
          {availableRoms.map(rom => (
            <option key={rom} value={rom}>{rom}</option>
          ))}
        </select>
        
        <button 
          onClick={() => selectedRom && loadRom(selectedRom)}
          disabled={!selectedRom || isPlaying}
          style={{ marginLeft: '10px' }}
        >
          Load ROM
        </button>
        
        <button 
          onClick={testRomLoad}
          disabled={isPlaying}
          style={{ marginLeft: '10px', backgroundColor: '#4CAF50', color: 'white' }}
        >
          Test ROM
        </button>

        {isLoaded && (
          <>
            <button onClick={togglePause} style={{ marginLeft: '10px' }}>
              {isPlaying ? 'Pause' : 'Resume'}
            </button>
            <button onClick={reset} style={{ marginLeft: '10px' }}>
              Reset
            </button>
          </>
        )}
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          {error}
        </div>
      )}

      <div className="nes-screen">
        <canvas
          ref={canvasRef}
          width={256}
          height={240}
          style={{
            border: '2px solid #000',
            imageRendering: 'pixelated',
            width: '512px',
            height: '480px',
            backgroundColor: '#000'
          }}
        />
      </div>

      <div className="nes-instructions" style={{ marginTop: '10px', fontSize: '12px' }}>
        <p><strong>Controls:</strong></p>
        <p>Arrow Keys: D-Pad | Z: A Button | X: B Button | Enter: Start | Right Shift: Select</p>
        <p>Click on the emulator area to focus keyboard controls.</p>
        {!selectedRom && (
          <p style={{ color: '#666' }}>
            Upload ROM files to the <code>/public/roms/nes/</code> folder to play games.
          </p>
        )}
      </div>
    </div>
  );
};

export default NESEmulator; 